import { getTool, getToolNames, patchSchema } from '@takeshape/schema';
import { useActiveSchema } from 'app/branches/hooks/use-active-schema';
import { useDebouncedChangeHandler } from 'app/common/hooks/use-debounced-change-handler';
import useInit from 'app/common/hooks/use-init';
import { showMessage } from 'app/messages/actions';
import { isEqualLooseUndefined } from 'app/schema/util/is-equal-loose-undefined';
import { resetForm, submitForm } from 'app/schema-form/actions';
import { getErrors } from 'app/schema-form/selectors.js';
import type { OnSubmit } from 'app/schema-form/types';
import cloneDeep from 'lodash/cloneDeep.js';
import set from 'lodash/fp/set.js';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TOOL_FORM_NAME, TOOL_WORKING_COPY_ORIGIN } from '../../constants';
import { useToolStore } from '../../tool-store.ts';
import type { ToolEditorFormState } from '../../types.js';
import { getSchemaUpdate } from '../../utils.js';
import { createFormConfig, createFormValidator, createFormWidgets } from './tool-editor-form.js';

export type ToolEditorHookProps = {
  toolName?: string;
};

export const useToolEditor = (props: ToolEditorHookProps) => {
  const { toolName } = props;

  const dispatch = useDispatch();
  const [currentToolName, setCurrentToolName] = useState<string | undefined>(toolName);
  const [isInitial, setIsInitial] = useState(true);
  const {
    activeSchemaCompat: workingCopy,
    dataOrigin: workingCopyDataOrigin,
    update: updateWorkingCopy,
    hasPendingChanges,
    setHasPendingChanges
  } = useActiveSchema();
  const lastToolStateRef = useRef<Partial<ToolEditorFormState>>();
  const formErrors = useSelector((state) => getErrors(state, { formName: TOOL_FORM_NAME }));
  const runEditPath = useToolStore((state) => state.runEditPath);

  const showError = useCallback((message: string) => dispatch(showMessage('error', message)), [dispatch]);

  const resetToolForm = useCallback(
    (data?: Partial<ToolEditorFormState>) => {
      dispatch(resetForm(TOOL_FORM_NAME, { initialData: data }));
      setCurrentToolName(data?.name);
    },
    [dispatch]
  );

  const updateToolName = useCallback((toolName: string) => {
    setCurrentToolName(toolName);
  }, []);

  const updateSchema = useDebouncedChangeHandler(
    useCallback(
      (data: ToolEditorFormState) => {
        if (!workingCopy || !data.name) {
          return;
        }

        try {
          const { toolName, schemaUpdate } = getSchemaUpdate(workingCopy, data, currentToolName);
          let patched = patchSchema(workingCopy, schemaUpdate);

          if (runEditPath) {
            patched = set(runEditPath, `Tool.${toolName}`, patched);
          }
          updateToolName(toolName);
          void updateWorkingCopy(patched, TOOL_WORKING_COPY_ORIGIN);
        } catch (e) {
          console.trace('Error updating working copy in tool editor', e);
          showError(e.message);
          setHasPendingChanges(false);
        }
      },
      [workingCopy, currentToolName, updateWorkingCopy, updateToolName, showError, setHasPendingChanges, runEditPath]
    ),
    1000
  ).run;

  const onSubmit: OnSubmit<ToolEditorFormState> = useCallback(
    ({ data, dirty }) => {
      const hasChanges = !isEqualLooseUndefined(lastToolStateRef.current, data);
      setHasPendingChanges(hasChanges);
      if (hasChanges) {
        updateSchema(data, dirty);
      }
    },
    [setHasPendingChanges, updateSchema]
  );

  const existingToolNames = useMemo(() => (workingCopy ? getToolNames(workingCopy) : []), [workingCopy]);
  const form = useMemo(() => createFormConfig({ onSubmit }), [onSubmit]);

  const formValidator = useMemo(
    () => createFormValidator(currentToolName, existingToolNames),
    [currentToolName, existingToolNames]
  );

  const formWidgets = useMemo(() => createFormWidgets(), []);

  const initialData: Partial<ToolEditorFormState> | undefined = useMemo(() => {
    if (!workingCopy) {
      return;
    }

    const formState = toolName
      ? cloneDeep({
          name: toolName,
          ...getTool(workingCopy, toolName)
        })
      : {};

    if (!isInitial && workingCopyDataOrigin !== TOOL_WORKING_COPY_ORIGIN) {
      resetToolForm(formState);
    }

    lastToolStateRef.current = formState;

    return formState;
  }, [toolName, workingCopy, workingCopyDataOrigin, resetToolForm, isInitial]);

  useInit({
    onInit() {
      setIsInitial(false);
    }
  });

  const handleSubmit = useCallback(() => {
    dispatch(submitForm(TOOL_FORM_NAME, onSubmit));
  }, [dispatch, onSubmit]);

  return {
    initialData,
    form,
    formValidator,
    formWidgets,
    handleSubmit,
    hasPendingChanges,
    hasErrors: formErrors.count() > 0
  };
};

export type ToolEditorProps = ReturnType<typeof useToolEditor>;
